import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter, useLocalSearchParams, useFocusEffect } from "expo-router";
import { useCallback, useRef, useState } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity, ActivityIndicator } from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { OnboardingBonusBanner } from "@/components/bonuses/OnboardingBonusBanner";
import { ActivationScreen } from "@/components/onboarding/ActivationScreen";
import { SubmissionCelebration } from "@/components/onboarding/SubmissionCelebration";
import { useOnboardingRefresh } from "@/hooks/useOnboardingWebSocket";
import { useAuth } from "@/lib/auth";
import { MOBILE_DEBUG_VERSION } from "@/lib/debug";
import { clearLocalFlag, getLocalFlag, setLocalFlag } from "@/lib/local-flags";
import {
    fetchOnboardingState,
    startOnboarding,
    type OnboardingState,
    type ObSectionNode
} from "@/lib/onboarding-api";
import { getAttentionItems } from "@/lib/onboarding-attention";
import {
    computeAllSubmitted,
    computeSectionProgress,
    isSectionLocked
} from "@/lib/onboarding-branching";
import { colors, fonts, gradients, radius, shadows, spacing, typography } from "@/lib/theme";

const SECTION_ICONS: Record<string, { icon: string; bg: string; color: string }> = {
    "Identity & Licensure": {
        icon: "card-account-details-outline",
        bg: "#E8FAF5",
        color: "#45D2B0"
    },
    "Medical & Health": { icon: "stethoscope", bg: "#E8FAF5", color: "#45D2B0" },
    "Insurance & Certificates": { icon: "shield-check-outline", bg: "#FFF0F7", color: "#E748F5" }
};

// ── Overall Progress Bar ────────────────────────────────────────────────────
function OverallProgressBar({ progress, total }: { progress: number; total: number }) {
    const pct = total > 0 ? Math.round((progress / total) * 100) : 0;
    const remaining = total - progress;
    return (
        <View style={styles.overallProgressWrap}>
            <View style={styles.overallProgressTextRow}>
                <Text style={styles.overallProgressLabel}>{remaining} items remaining</Text>
                <Text style={styles.overallProgressPct}>{pct}%</Text>
            </View>
            <View style={styles.overallProgressTrack}>
                <LinearGradient
                    colors={[colors.teal400, colors.pink400]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0.2 }}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    style={[styles.overallProgressFill, { width: `${pct}%` as any }]}
                />
            </View>
        </View>
    );
}

// ── Section Card ────────────────────────────────────────────────────────────
function SectionCard({
    section,
    progress,
    total,
    onPress,
    locked
}: {
    section: ObSectionNode;
    progress: number;
    total: number;
    onPress: () => void;
    locked?: boolean;
}) {
    const isComplete = total > 0 && progress === total;
    const allItems = section.subsections.flatMap((sub) => sub.items);
    const attentionItems = locked ? [] : getAttentionItems(allItems);
    const pct = total > 0 ? Math.round((progress / total) * 100) : 0;
    const isZero = pct === 0;
    const isHigh = pct >= 70;
    const remaining = total - progress;
    const iconCfg = SECTION_ICONS[section.name];

    return (
        <TouchableOpacity
            style={[styles.card, isComplete && styles.cardComplete, locked && styles.cardLocked]}
            activeOpacity={locked ? 1 : 0.7}
            onPress={locked ? undefined : onPress}
            disabled={locked}
        >
            <View style={styles.cardTop}>
                {/* Icon */}
                <View
                    style={[
                        styles.cardIcon,
                        isComplete && styles.cardIconComplete,
                        !locked &&
                            iconCfg && {
                                backgroundColor: isComplete ? colors.success50 : iconCfg.bg
                            }
                    ]}
                >
                    <MaterialCommunityIcons
                        name={
                            (locked
                                ? "lock-outline"
                                : isComplete
                                  ? "check-circle"
                                  : (iconCfg?.icon ??
                                    "folder-outline")) as keyof typeof MaterialCommunityIcons.glyphMap
                        }
                        size={22}
                        color={
                            locked
                                ? colors.textSecondary
                                : isComplete
                                  ? colors.success500
                                  : (iconCfg?.color ?? colors.teal600)
                        }
                    />
                </View>

                {/* Name + sub */}
                <View style={styles.cardInfo}>
                    <Text style={[styles.cardTitle, locked && styles.cardTitleLocked]}>
                        {section.name}
                    </Text>
                    <Text style={styles.cardSub}>
                        {locked
                            ? "Complete your other sections to unlock"
                            : isComplete
                              ? "Complete"
                              : isZero
                                ? `${total} items`
                                : remaining <= 3
                                  ? `${remaining} items left \u00B7 Almost there!`
                                  : `${remaining} items left`}
                    </Text>
                </View>

                {/* Right: percentage + chevron, or a lock */}
                <View style={styles.cardRight}>
                    {locked ? (
                        <MaterialCommunityIcons
                            name="lock"
                            size={18}
                            color={colors.textSecondary}
                        />
                    ) : (
                        <>
                            <Text
                                style={[
                                    styles.cardPct,
                                    !isZero && styles.cardPctHigh,
                                    isZero && styles.cardPctZero
                                ]}
                            >
                                {pct}%
                            </Text>
                            <MaterialCommunityIcons
                                name="chevron-right"
                                size={20}
                                color="#D1D5DB"
                            />
                        </>
                    )}
                </View>
            </View>

            {/* Progress bar — pink if has issues, teal if clean, gray if zero */}
            {!locked && total > 0 && (
                <View style={styles.cardProgressTrack}>
                    <View
                        style={[
                            styles.cardProgressFill,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            { width: `${pct}%` as any },
                            attentionItems.length > 0 && { backgroundColor: colors.pink400 },
                            attentionItems.length === 0 &&
                                !isZero && { backgroundColor: colors.teal400 },
                            isZero && { backgroundColor: "#D1D5DB" }
                        ]}
                    />
                </View>
            )}

            {/* Attention tags (pink pills) */}
            {attentionItems.length > 0 && (
                <View style={styles.tagRow}>
                    {attentionItems.map((a, i) => (
                        <View key={i} style={styles.tag}>
                            <View style={styles.tagDot} />
                            <Text style={styles.tagText}>
                                {a.itemName}{" "}
                                {a.type === "pending"
                                    ? "pending"
                                    : a.type === "waiting_on_you"
                                      ? "needs your photo ID"
                                      : a.type}
                            </Text>
                        </View>
                    ))}
                </View>
            )}

            {/* Start section button for 0% sections */}
            {!locked && isZero && !isComplete && (
                <TouchableOpacity style={styles.startBtn} onPress={onPress}>
                    <MaterialCommunityIcons name="plus" size={14} color={colors.surface} />
                    <Text style={styles.startBtnText}>START SECTION</Text>
                </TouchableOpacity>
            )}
        </TouchableOpacity>
    );
}

// ── Main Screen ─────────────────────────────────────────────────────────────
export default function OnboardingMainScreen() {
    const router = useRouter();
    const { rnId: rnIdParam } = useLocalSearchParams<{ rnId: string }>();
    const { user } = useAuth();
    const [state, setState] = useState<OnboardingState | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showActivation, setShowActivation] = useState(false);
    const [showSubmitted, setShowSubmitted] = useState(false);
    // Tracks the previously-seen activation state so we can fire the celebration
    // exactly on the false -> true transition (e.g. the last must_before item is
    // approved and a WebSocket refresh flips activatedAt), not on every load of
    // an already-activated RN. null = not yet loaded.
    const wasActivatedRef = useRef<boolean | null>(null);

    const rnId = rnIdParam ?? (user?.rnId ? String(user.rnId) : "");

    useFocusEffect(
        useCallback(() => {
            loadOnboarding();
        }, [rnId])
    );

    // Live updates via WebSocket — progress bar and badges update instantly
    useOnboardingRefresh(rnId, loadOnboarding);

    // Fire the activation celebration on the not-activated -> activated edge.
    function maybeCelebrateActivation(data: OnboardingState) {
        const isActivated = !!data.onboarding.activatedAt;
        if (wasActivatedRef.current === false && isActivated) {
            setShowActivation(true);
        }
        wasActivatedRef.current = isActivated;
    }

    // Fire the "application submitted" celebration ONCE, the first time every
    // required must_before item is handed in. Persisted (not a ref) so a
    // remount or app restart doesn't re-celebrate; activation supersedes it.
    async function maybeCelebrateSubmission(data: OnboardingState) {
        if (data.onboarding.activatedAt) {
            return;
        }
        const flagKey = `onboarding_submitted_celebrated_${data.onboarding.rnId}`;

        if (!computeAllSubmitted(data.config)) {
            // She is no longer fully handed in — a document was bounced back,
            // or her application was reopened. Clear the one-shot flag so
            // finishing again celebrates again.
            //
            // Without this the flag is burned for the life of the install: a
            // nurse who has one item rejected, fixes it, and completes a second
            // time gets nothing, and there is no way to reach the flag from
            // outside the device.
            await clearLocalFlag(flagKey);
            return;
        }

        if (await getLocalFlag(flagKey)) {
            return;
        }
        await setLocalFlag(flagKey, "1");
        setShowSubmitted(true);
    }

    async function loadOnboarding() {
        if (!rnId) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await fetchOnboardingState(rnId);
            setState(data);
            maybeCelebrateActivation(data);
            void maybeCelebrateSubmission(data);
        } catch (err: unknown) {
            // If onboarding not started yet, start it
            const errMsg = err instanceof Error ? err.message : "";
            if (errMsg.includes("not started") || errMsg.includes("not found")) {
                try {
                    await startOnboarding(rnId);
                    const data = await fetchOnboardingState(rnId);
                    setState(data);
                } catch (startErr: unknown) {
                    // 409 means already started — just retry fetch
                    const startErrMsg = startErr instanceof Error ? startErr.message : "";
                    if (startErrMsg.includes("already started")) {
                        const data = await fetchOnboardingState(rnId);
                        setState(data);
                    } else {
                        setError(startErrMsg || "Failed to start onboarding");
                    }
                }
            } else {
                setError(errMsg || "Failed to load onboarding");
            }
        } finally {
            setLoading(false);
        }
    }

    // Visibility-aware section progress computed from the config — same helper the
    // section screen uses, so the home "N items left / X%" matches it exactly
    // (the old backend counts ignored branching, causing e.g. "3 left/80%" here
    // vs "2 left/85%" on the section page).
    function getSectionProgress(sectionId: string) {
        const section = state?.config.find((s) => String(s.id) === String(sectionId));
        if (!section) {
            return { approved: 0, total: 0 };
        }
        return computeSectionProgress(section);
    }

    if (loading) {
        return (
            <View style={styles.center}>
                <ActivityIndicator size="large" color={colors.teal400} />
                <Text style={styles.loadingText}>Loading your onboarding...</Text>
            </View>
        );
    }

    if (error) {
        return (
            <View style={styles.center}>
                <MaterialCommunityIcons
                    name="alert-circle-outline"
                    size={48}
                    color={colors.error500}
                />
                <Text style={styles.errorText}>{error}</Text>
                <TouchableOpacity onPress={loadOnboarding}>
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={styles.retryButton}
                    >
                        <Text style={styles.retryText}>Retry</Text>
                    </LinearGradient>
                </TouchableOpacity>
            </View>
        );
    }

    if (!state) {
        return null;
    }

    const totalApproved = state.progress.totalApproved;
    const totalRequired = state.progress.totalRequired;
    const tabsHref = "/(tabs)";

    // Separate must-before and can-after sections
    const mustBeforeSections = state.config.filter((s) => s.activationGate === "must_before");
    const canAfterSections = state.config.filter((s) => s.activationGate === "can_after");

    // Everything handed in, not yet activated → the application is under
    // review; explain that instead of leaving a silent dead-end.
    const underReview = !state.onboarding.activatedAt && computeAllSubmitted(state.config);

    return (
        <SafeAreaView edges={["top", "bottom"]} style={styles.container}>
            {showActivation && <ActivationScreen onDismiss={() => setShowActivation(false)} />}
            {showSubmitted && !showActivation && (
                <SubmissionCelebration
                    firstName={user?.firstName ?? null}
                    onDismiss={() => setShowSubmitted(false)}
                />
            )}
            <ScrollView contentContainerStyle={styles.scrollContent}>
                <View style={styles.topBar}>
                    <TouchableOpacity
                        style={styles.backArrow}
                        hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                        onPress={() => {
                            console.info(
                                `[mobile ${MOBILE_DEBUG_VERSION}] onboarding root back pressed`,
                                {
                                    tabsHref
                                }
                            );
                            router.navigate(tabsHref);
                        }}
                    >
                        <MaterialCommunityIcons
                            name="chevron-left"
                            size={28}
                            color={colors.textPrimary}
                        />
                    </TouchableOpacity>
                    <View style={styles.topBarSpacer} />
                </View>

                {/* Header — gradient text */}
                <View style={styles.headerTitleRow}>
                    {"Onboarding".split("").map((ch, i) => {
                        // Match 135deg diagonal gradient from mockup
                        const gradientColors = [
                            "#45D2B0", // O - teal
                            "#56C4AD", // n - teal
                            "#6BB5AB", // b - muted teal
                            "#82A3B2", // o - blue-gray
                            "#9590BA", // a - gray-purple
                            "#A87EC2", // r - muted purple
                            "#BB6CCA", // d - purple
                            "#CE5AD2", // i - purple-pink
                            "#D951DB", // n - pink
                            "#E748F5" // g - pink
                        ];
                        return (
                            <Text
                                key={i}
                                style={[styles.headerTitle, { color: gradientColors[i] }]}
                            >
                                {ch}
                            </Text>
                        );
                    })}
                </View>
                <Text style={styles.headerSubtitle}>
                    Verify your credentials to start accepting medical shifts.
                </Text>

                {/* Under review: everything is handed in — say what happens next */}
                {underReview && (
                    <View style={styles.reviewCard}>
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.reviewIconCircle}
                        >
                            <MaterialCommunityIcons
                                name="clipboard-clock-outline"
                                size={22}
                                color={colors.surface}
                            />
                        </LinearGradient>
                        <View style={styles.reviewTextWrap}>
                            <Text style={styles.reviewTitle}>Application under review</Text>
                            <Text style={styles.reviewBody}>
                                You&apos;ve completed your onboarding — amazing job. Our team is
                                reviewing your application and we&apos;ll notify you the moment
                                you&apos;re activated.
                            </Text>
                        </View>
                    </View>
                )}

                {/* The $25 joining bonus — renders only when the API sends an offer */}
                <OnboardingBonusBanner offer={state.bonusOffer} />

                {/* Overall progress */}
                <OverallProgressBar progress={totalApproved} total={totalRequired} />

                {/* Must-before sections (the locked group unlocks once the rest are done) */}
                {mustBeforeSections.map((section) => {
                    const { approved, total } = getSectionProgress(section.id);
                    return (
                        <SectionCard
                            key={section.id}
                            section={section}
                            progress={approved}
                            total={total}
                            locked={isSectionLocked(section, state.config)}
                            onPress={() =>
                                router.push(`/onboarding/section/${section.id}?rnId=${rnId}`)
                            }
                        />
                    );
                })}

                {/* Can-after sections — only shown after RN is activated */}
                {canAfterSections.length > 0 && !!state.onboarding.activatedAt && (
                    <>
                        <View style={styles.divider}>
                            <View style={styles.dividerLine} />
                            <Text style={styles.dividerText}>After Activation</Text>
                            <View style={styles.dividerLine} />
                        </View>

                        {canAfterSections.map((section) => {
                            const { approved, total } = getSectionProgress(section.id);
                            return (
                                <SectionCard
                                    key={section.id}
                                    section={section}
                                    progress={approved}
                                    total={total}
                                    onPress={() =>
                                        router.push(
                                            `/onboarding/section/${section.id}?rnId=${rnId}`
                                        )
                                    }
                                />
                            );
                        })}
                    </>
                )}
            </ScrollView>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    scrollContent: {
        padding: spacing.base + 8,
        paddingBottom: 40,
        gap: 14
    },
    center: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        gap: spacing.md,
        padding: spacing.xl
    },
    loadingText: {
        ...typography.body,
        color: colors.textSecondary,
        marginTop: spacing.sm
    },
    errorText: {
        ...typography.body,
        color: colors.error500,
        textAlign: "center"
    },
    retryButton: {
        paddingHorizontal: spacing.lg,
        paddingVertical: spacing.sm + 2,
        borderRadius: radius.full
    },
    retryText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.surface
    },

    // ── Top bar ──
    topBar: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 8
    },
    backArrow: {
        width: 44,
        height: 44,
        borderRadius: 12,
        alignItems: "center",
        justifyContent: "center",
        marginLeft: -10
    },
    topBarSpacer: {
        width: 34
    },

    // ── Header ──
    headerTitleRow: {
        flexDirection: "row",
        marginBottom: 10
    },
    headerTitle: {
        fontSize: 38,
        fontFamily: fonts.extraBold,
        lineHeight: 42
    },
    headerSubtitle: {
        fontSize: 15,
        fontFamily: fonts.regular,
        color: "#6B7280",
        lineHeight: 22,
        marginBottom: 24
    },

    // ── Under-review card ──
    reviewCard: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: colors.teal200,
        padding: spacing.base,
        marginBottom: spacing.lg,
        ...shadows.sm
    },
    reviewIconCircle: {
        width: 40,
        height: 40,
        borderRadius: 20,
        alignItems: "center",
        justifyContent: "center"
    },
    reviewTextWrap: {
        flex: 1
    },
    reviewTitle: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        marginBottom: 4
    },
    reviewBody: {
        fontSize: 13,
        fontFamily: fonts.regular,
        color: colors.textSecondary,
        lineHeight: 19
    },

    // ── Overall progress ──
    overallProgressWrap: {
        gap: spacing.sm,
        marginBottom: 8
    },
    overallProgressTextRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
    },
    overallProgressLabel: {
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: "#374151"
    },
    overallProgressPct: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.teal400
    },
    overallProgressTrack: {
        height: 8,
        borderRadius: 100,
        backgroundColor: "#E5E7EB",
        overflow: "hidden"
    },
    overallProgressFill: {
        height: "100%",
        borderRadius: 100
    },

    // ── Card ──
    card: {
        backgroundColor: "#FAFAFA",
        borderRadius: 16,
        borderWidth: 1,
        borderColor: "#F0F0F0",
        padding: 18
    },
    cardComplete: {
        borderColor: colors.success400
    },
    cardLocked: {
        opacity: 0.6,
        backgroundColor: "#F3F4F6"
    },
    cardTitleLocked: {
        color: colors.textSecondary
    },
    cardTop: {
        flexDirection: "row",
        alignItems: "center",
        gap: 14
    },
    cardIcon: {
        width: 44,
        height: 44,
        borderRadius: 12,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center"
    },
    cardIconComplete: {
        backgroundColor: colors.success50
    },
    cardInfo: {
        flex: 1
    },
    cardTitle: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: "#1F2937",
        marginBottom: 2
    },
    cardSub: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: "#9CA3AF"
    },
    cardRight: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8
    },
    cardPct: {
        fontSize: 18,
        fontFamily: fonts.extraBold,
        color: colors.pink400
    },
    cardPctHigh: {
        color: colors.teal400
    },
    cardPctZero: {
        color: "#D1D5DB"
    },

    // ── Card progress bar ──
    cardProgressTrack: {
        height: 4,
        borderRadius: 100,
        backgroundColor: "#E5E7EB",
        marginTop: 12,
        overflow: "hidden"
    },
    cardProgressFill: {
        height: "100%",
        borderRadius: 100
    },

    // ── Attention tags (pink pills) ──
    tagRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
        marginTop: 12
    },
    tag: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingHorizontal: 10,
        paddingVertical: 4,
        backgroundColor: "#FDF0FF",
        borderWidth: 1,
        borderColor: "#F5D0FE",
        borderRadius: 100
    },
    tagDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: colors.pink400
    },
    tagText: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: colors.pink400
    },

    // ── Start section button ──
    startBtn: {
        flexDirection: "row",
        alignItems: "center",
        alignSelf: "flex-start",
        gap: 6,
        marginTop: 12,
        paddingVertical: 8,
        paddingHorizontal: 16,
        backgroundColor: colors.teal400,
        borderRadius: 8
    },
    startBtnText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.surface,
        letterSpacing: 0.8,
        textTransform: "uppercase"
    },

    // ── Divider ──
    divider: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        marginVertical: spacing.xs
    },
    dividerLine: {
        flex: 1,
        height: 1,
        backgroundColor: colors.border
    },
    dividerText: {
        ...typography.caption,
        color: colors.textTertiary
    }
});
